import Link from "@tiptap/extension-link";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Code,
  Heading2,
  Heading3,
  Italic,
  Link2,
  Link2Off,
  List,
  ListOrdered,
  Quote,
  Redo,
  Strikethrough,
  Undo,
} from "lucide-react";
import { useCallback, useEffect } from "react";

/**
 * The event description editor.
 *
 * Its toolbar is deliberately the shape of the server's allowlist. The
 * backend sanitizes with nh3 against a tag set named `_TIPTAP_TAGS`
 * (a, blockquote, code, em, h1-h6, hr, li, ol, p, pre, s, strong, u, ul) and
 * permits only `https` and `mailto` links. Offering a control whose output the
 * sanitizer strips would let an admin save formatting that silently vanishes,
 * so every control here maps to something that survives — and the link
 * protocols below are pinned to the same two schemes for the same reason.
 *
 * Two allowed tags are deliberately not offered: `h1`, because the page
 * already supplies the event title as its only h1, and `u`, because underline
 * on the web reads as a link and invites mis-clicks.
 */
export function RichTextEditor({
  value,
  onChange,
  disabled,
  testId,
}: {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  testId?: string;
}) {
  const editor = useEditor({
    extensions: [
      // StarterKit ships its own Link; turn it off so ours is the only one and
      // the protocol restriction below actually applies.
      StarterKit.configure({ link: false }),
      Link.configure({
        openOnClick: false,
        protocols: ["https", "mailto"],
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: current }) => onChange(current.getHTML()),
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-sm:prose-base min-h-40 max-w-none px-3 py-2 focus:outline-hidden dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-a:text-lit",
        "data-testid": testId ?? "rich-text-editor",
      },
    },
  });

  // Keep the editor in step when the document is reloaded underneath it, but
  // never while the user is typing: setContent would move their cursor.
  useEffect(() => {
    if (!editor) return;
    if (editor.isFocused) return;
    if (editor.getHTML() === value) return;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const current = editor.getAttributes("link").href as string | undefined;
    const entered = window.prompt(
      "Link URL (https:// or mailto:)",
      current ?? "https://",
    );
    if (entered === null) return;
    const href = entered.trim();
    if (!href) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    // Match the server: anything else is stripped on save, so refuse it here
    // rather than accept it and lose it.
    if (!/^(https:\/\/|mailto:)/i.test(href)) {
      window.alert("Links must start with https:// or mailto:");
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="rounded-xl border border-input bg-background">
      <div className="flex flex-wrap gap-1 border-b border-input px-2 py-1.5">
        <ToolbarButton
          editor={editor}
          label="Heading"
          icon={Heading2}
          isActive={editor.isActive("heading", { level: 2 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        />
        <ToolbarButton
          editor={editor}
          label="Subheading"
          icon={Heading3}
          isActive={editor.isActive("heading", { level: 3 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        />
        <span className="mx-1 w-px bg-border" />
        <ToolbarButton
          editor={editor}
          label="Bold"
          icon={Bold}
          isActive={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          editor={editor}
          label="Italic"
          icon={Italic}
          isActive={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolbarButton
          editor={editor}
          label="Strikethrough"
          icon={Strikethrough}
          isActive={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />
        <span className="mx-1 w-px bg-border" />
        <ToolbarButton
          editor={editor}
          label="Link"
          icon={Link2}
          isActive={editor.isActive("link")}
          onClick={setLink}
        />
        <ToolbarButton
          editor={editor}
          label="Remove link"
          icon={Link2Off}
          disabled={!editor.isActive("link")}
          onClick={() => editor.chain().focus().unsetLink().run()}
        />
        <span className="mx-1 w-px bg-border" />
        <ToolbarButton
          editor={editor}
          label="Bullet list"
          icon={List}
          isActive={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolbarButton
          editor={editor}
          label="Numbered list"
          icon={ListOrdered}
          isActive={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <ToolbarButton
          editor={editor}
          label="Quote"
          icon={Quote}
          isActive={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <ToolbarButton
          editor={editor}
          label="Code"
          icon={Code}
          isActive={editor.isActive("code")}
          onClick={() => editor.chain().focus().toggleCode().run()}
        />
        <span className="mx-1 w-px bg-border" />
        <ToolbarButton
          editor={editor}
          label="Undo"
          icon={Undo}
          onClick={() => editor.chain().focus().undo().run()}
        />
        <ToolbarButton
          editor={editor}
          label="Redo"
          icon={Redo}
          onClick={() => editor.chain().focus().redo().run()}
        />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

function ToolbarButton({
  label,
  icon: Icon,
  isActive,
  disabled,
  onClick,
  editor,
}: {
  label: string;
  icon: React.ElementType;
  isActive?: boolean;
  disabled?: boolean;
  onClick: () => void;
  editor: Editor;
}) {
  // The hover label repeats the aria-label: the icons are the only thing a
  // sighted person has to go on, and several of them (quote, code,
  // strikethrough) are guesswork at 16px. It hangs on the wrapper rather than
  // the button because a disabled button receives no pointer events, and
  // "Remove link" is disabled exactly when somebody is most likely to hover it.
  return (
    <span className="inline-flex" title={label}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={isActive ?? false}
        disabled={disabled || !editor.isEditable}
        onClick={onClick}
        className={`flex items-center justify-center rounded-md p-1.5 transition-colors max-sm:size-11 disabled:opacity-40 ${
          isActive
            ? "bg-secondary text-secondary-foreground"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        }`}
      >
        <Icon className="h-4 w-4" />
      </button>
    </span>
  );
}
